import { useDispatch, useSelector } from "react-redux";
import { selectCurrentLang, selectTranslations } from "../../services/i18n/i18nSlice";
import { useLocation } from "react-router";
import { useEffect, useState, useRef } from "react";
import { setLoading, setNavbarHide, setNavbarHideMobile } from "../../services/global/globalSlice";
import { Link, useNavigate } from "react-router-dom";

import correct_img from '../../assets/images/questions/correct.svg'
import missing_img from '../../assets/images/questions/missing.svg'
import wrong_img from '../../assets/images/questions/wrong.svg'
import timer_img from './assets/images/timer.svg'
import score_img from './assets/images/score.svg'
import questions_img from '../../assets/images/questions.svg'
import hand_img from '../../assets/images/high-five-hand.svg'
import sad_img from '../../assets/images/sad.svg'
import structure_img from './assets/images/structure.svg'

import { TitleH1 } from "../../components/TitleH1";
import { TitleH3 } from "../../components/TitleH3";
import { Button } from "../../components/Button";
import { formatScore, normalizeModules, getOutcomeThreshold } from "./examUtils";
import ThresholdSheet from "./_ofa_components/ThresholdSheet";
import { selectAppInfo } from "../../services/utils/appInfoSlice";
import { selectUserData } from "../../features/User/userSlice";
import Scoreboard from "./_components/Scoreboard";
import { exitFullscreen } from "../../utils/deviceManagement";
import { loadCurrentNotificationDebounced } from "../../features/Notification/notificationSlice";
import { use } from "react";
import { OFA_RECOVERY_TENANT, TENANT } from "../../config/constants";
import { fetchGetOfaSubjects } from "../../features/User/userApi";

export default function ExamEndScreen(){

    const [examData, setExamData] = useState(null)
    const [step, setStep] = useState(0)
    const [examId, setExamId] = useState("")
    const [userExamId, setUserExamId] = useState("")

    const appInfo = useSelector(selectAppInfo)
    // the ofa platforms run for a university (unisr) show the outcome like the ofa tenants
    const isOfa = appInfo?.is_ofa || OFA_RECOVERY_TENANT.includes(TENANT)
    const userInfo = useSelector(selectUserData)

    const t = useSelector(selectTranslations)

    const lang = useSelector(selectCurrentLang)
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [showThresholdSheet, setShowThresholdSheet] = useState(false)

    const closeThresholdSheet = (_,redirect = true) =>{
        if(redirect)
            navigate('/',{replace: true})
        setShowThresholdSheet(false)
    }

    useEffect(()=>{
        dispatch(setNavbarHide(true))
        if(location.state?.exam_id)
            setExamId(location.state?.exam_id)
        const _userExamId = location.state?.results?.user_exam_id || location.state?.examData?.user_exam_id
        if(_userExamId)
            setUserExamId(_userExamId)
        if(location.state && location.state?.results){
            const {_questions} = normalizeModules(location.state?.results?.modules)
            setExamData({
                ...location.state.results,
                questions: _questions,
                papers: location?.state?.examData?.papers
            })
        } else if (location.state?.examData){
            setExamData(location.state?.examData)
        } else
            navigate('/exams', {replace:true})
        dispatch(setLoading(false))
        // eslint-disable-next-line
    },[location])

    useEffect(() => {
        dispatch(setNavbarHideMobile(true))
        dispatch(setNavbarHide(true))
        setTimeout(()=>{
            setShowThresholdSheet(true)
        },2000)
    }, [examData?.exam_id])

    useEffect(() => {
        if(examData?.proctoring_required)
            exitFullscreen()
    }, [examData?.proctoring_required])

    useEffect(() => {
        if (examData?.thresholds?.some(th => th?.threshold_type === 'add_to_group_threshold')
        && appInfo?.personalizations?.enabled_sections?.notifications) {
            dispatch(loadCurrentNotificationDebounced());
        }
    }, [examData?.thresholds])

    const totalTime = () => {
        let _total = 0
        examData?.modules?.forEach((m)=>
            _total += m.consumed_time
        )
        return _total
    }

    const formatTime = (time) => {
        return String(time).padStart(2, '0')
    }

    const allThresholds = [...(examData?.thresholds || []), ...(examData?.modules || []).flatMap(m => m?.thresholds || [])]
    const outcomeThreshold = getOutcomeThreshold(examData)

    // a failed exam with multiple attempts can be taken again resumable_after (seconds) after the attempt ended
    const canRetry = isOfa && outcomeThreshold && !outcomeThreshold.has_been_passed && examData?.is_multiple_attempts
    const retryDays = Math.ceil((examData?.resumable_after || 0) / 86400)
    const retryDate = new Date(Date.now() + (examData?.resumable_after || 0) * 1000).toLocaleDateString(lang, {day: 'numeric', month: 'long'})
    const retryText = !retryDays ? t.ofa.retry.now
        : (retryDays === 1 ? t.ofa.retry.tomorrow : t.ofa.retry.in_days.replace('{days}', retryDays)).replace('{date}', retryDate)

    // the student is done when no ofa subject is left to recover; tenants without ofa subjects
    // fall back to the subject threshold of this exam
    const [ofaSubjects, setOfaSubjects] = useState(null)
    useEffect(() => {
        if (isOfa) fetchGetOfaSubjects().then(response => response?.success && setOfaSubjects(response.data))
        // eslint-disable-next-line
    }, [])
    const checkAllOfaPassed = () => {
        if (ofaSubjects?.missing_subjects?.length || ofaSubjects?.recovered_subjects?.length)
            return !ofaSubjects.missing_subjects?.length
        return !!allThresholds.find(t => t.threshold_type === "subject_passing_score_threshold" && t.has_been_passed)
    }

    return (
        <section id="examPreviewScreen" className="end_screen">
            <div className="h-100">
            {
                examData?.visible_scoreboard ?
                    <div className="steps">
                        <div className={`bullet ${step === 0 ? 'active' : ''} `}></div>
                        <div className={`bullet ${step === 1 ? 'active' : ''} `}></div> 
                    </div> 
                : null
            }
            {examData && step === 0 ?
            <div className="d-flex flex-column justify-content-between h-100">
                <div style={{maxWidth:"500px", minWidth:"320px"}} className="mx-auto">
                    {
                        isOfa ?
                        <>
                            {outcomeThreshold?.has_been_passed ?
                                <img src={hand_img} style={{width:'45px'}} className="mx-auto d-block" alt=''/>
                                : 
                                <img src={sad_img} style={{width:'45px'}} className="mx-auto d-block" alt=''/>
                            }
                        </> :
                        <img src={hand_img} style={{width:'45px'}} className="mx-auto d-block" alt=''/>
                    }
                    <TitleH1 className="text-center">
                        {
                            isOfa && outcomeThreshold?.is_showable ?
                                <>
                                    {outcomeThreshold?.has_been_passed ?
                                        t.ofa.exam.exam_passed_title : t.ofa.exam.exam_not_passed_title
                                    }
                                </> :
                            t.exam.exam_ended
                        }
                    </TitleH1>
                    {canRetry &&
                        <div className="d-flex align-items-start mt-3 p-3" style={{background:'#E6ECF1', borderLeft:'3px solid var(--primary-color)', borderRadius:'4px'}}>
                            <img src={timer_img} alt="" className="me-2" style={{width:'24px'}} />
                            <div>
                                <p className="mb-1" style={{fontWeight:600, color:'#333'}}>{retryText}</p>
                                <p className="mb-0" style={{fontSize:'14px', color:'#54585A'}}>{t.ofa.retry.hint}</p>
                            </div>
                        </div>
                    }
                    <div className="border mt-4 mb-2 p-3 pt-4" style={{borderRadius:"16px", background:'#FFFDFC'}}>
                        <TitleH3  className="text-center"><img src={score_img} alt="" className="me-2"/> {t.exam.scoring}</TitleH3>
                        <div className="mt-2">
                            <div className="exam_score text-center">
                                <strong style={{color:'#181818'}}>{formatScore(examData?.total_score)}</strong> {t.exam.points}
                            </div>
                            <div className="d-flex justify-content-around mt-2">
                                <div className="d-flex flex-column justify-content-center align-items-center">
                                    <img style={{verticalAlign:"sub"}} src={correct_img} className="mb-1" alt="" />
                                    <strong>{examData?.correct_count}</strong> 
                                    <span>{t.exam.correct}</span>
                                </div>
                                <div className="d-flex flex-column justify-content-center align-items-center"><img style={{verticalAlign:"sub"}} src={missing_img} className="mb-1" alt="" /><strong>{examData?.missing_count}</strong><span>{t.exam.missing}</span> </div>
                                <div className="d-flex flex-column justify-content-center align-items-center"><img style={{verticalAlign:"sub"}} src={wrong_img} className="mb-1" alt="" /><strong>{examData?.wrong_count}</strong> <span>{t.exam.wrong}</span></div>
                            </div>
                        </div>
                    </div>
                    <div className="border mt-3 mb-2 p-3 pt-4" style={{borderRadius:"16px", background:'#FFFDFC'}}>
                        <TitleH3 className="text-center"><img src={timer_img} alt="" className="me-2"/>{t.exam.duration}</TitleH3>
                        <div className="mb-2  mt-3  ">
                            <div className="exam_score  text-center">
                                <strong style={{color:'#181818'}}>{totalTime()} min</strong>/ {examData.time}
                            </div>
                        </div> 
                    </div>
                    {
                        (examData?.is_self_assessment_test || isOfa) &&
                        <div className="border mt-3 mb-2 p-3 pt-4" style={{borderRadius:"16px", background:'#FFFDFC'}}>
                            <TitleH3 className="text-center"><img src={structure_img} alt="" className="me-2"/>{t.exam.modules}</TitleH3>
                            {examData?.thresholds?.map((threshold,i)=>{
                                if(threshold?.threshold_type === "standard_threshold")
                                    return(
                                        <div className="mb-2" key={i}>
                                            <span>{threshold.threshold_name}</span>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>{formatScore(examData?.total_score)} {t.exam.pt}</span>
                                                <span>{threshold?.has_been_passed ? <span className="text-success">{t.ofa?.scoring.passed}</span> : <span className="text-danger">{t.ofa?.scoring.not_passed}</span>}</span>
                                            </div>
                                            <hr />
                                        </div>
                                    )
                                else
                                    return null
                                })
                            }
                            {examData?.modules?.map((m,i)=>{
                                if(m?.thresholds?.find(t=> t?.threshold_type === "standard_threshold"))
                                    return(
                                        <div className="mb-2" key={i}>
                                            <span>{m.module_name}</span>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>{formatScore(m?.total_score)} {t.exam.pt}</span>
                                                <span>{m?.thresholds?.find(t=> t?.threshold_type === "standard_threshold" && t?.has_been_passed) ? <span className="text-success">{t.ofa?.scoring.passed}</span> : <span className="text-danger">{t.ofa?.scoring.not_passed}</span>}</span>
                                            </div>
                                            <hr />
                                        </div>
                                    )
                                else
                                    return null
                                })
                            }
                        </div>
                    }
                </div>
                <div>
                    {
                        examData?.questions?.length > 0 ?
                            <Link to={'/exams/review/'} state={{examData: examData, examId: examId}} replace={true}>
                                <Button className="mx-auto w-100">
                                    <img src={questions_img} alt="" className="me-1"/>
                                    {t.exam.questions_review}
                                </Button>
                            </Link>
                        : null
                    }
                    {
                        examData.visible_scoreboard ?
                            <div className="d-flex justify-content-center mx-auto mt-3" style={{maxWidth:"360px"}}>
                                <Button color="secondary" disabled={true} className="me-2 w-50">
                                   {t.buttons.back}
                                </Button>
                                <Button color="secondary" className="w-50" onClick={()=>{setStep(1)}}>
                                    {t.buttons.next}
                                </Button>
                            </div>
                        :
                        <>
                            {
                                examData?.is_self_assessment_test ?
                                <Link 
                                    to={`/exams/exam_detailed_results/${userExamId}`} 
                                    replace={true}
                                >
                                    <Button color="primary" className="mx-auto mt-2 w-100 pointer">
                                        {t.buttons.continue}
                                    </Button>
                                </Link> : 
                                <Link 
                                    to={isOfa && checkAllOfaPassed() ? '/' : '/exams/'} 
                                    replace={true}
                                >
                                    <Button color="secondary" className="mx-auto mt-2 w-100">
                                        {isOfa && !checkAllOfaPassed() ? t.buttons.keep_training : t.buttons.exit}
                                    </Button>
                                </Link> 
                            }
                        </>
                            
                    }
                    
                </div>
            </div>
            : null}
            {examData && step === 1 ?
            <div className="d-flex flex-column justify-content-between h-100">
                <div style={{maxWidth:"500px", minWidth:"320px", height:"80%"}} className="mx-auto">
                    <TitleH1 className="text-center">
                        {
                            t.scoreboard.title
                        }
                    </TitleH1>
                    <div style={{height:"100%"}}>
                        <Scoreboard examId={examId}  />
                    </div>
                </div>
                <div>
                    <div>
                        <Button color="secondary" onClick={()=>{setStep(0)}} className="mx-auto mt-2 w-100">
                            {t.buttons.back}
                        </Button>
                        <Link 
                            to={ isOfa && !outcomeThreshold?.has_been_passed ? '/' : '/exams/'} 
                            replace={true}
                        >
                            <Button className="mx-auto mt-2 w-100">
                                {isOfa && !outcomeThreshold?.has_been_passed ? t.buttons.keep_training : t.buttons.exit}
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
            : null}
            </div>
        </section>
    )
}